import React, { useState, useEffect } from 'react';
import { Trophy, TrendingDown, DollarSign, BarChart2, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

function Statistics() {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [amountWon, setAmountWon] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch current user's friends from Firestore
  useEffect(() => {
    const fetchFriends = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const userFriends = userData.friends || [];
        setFriends(userFriends);
        fetchOverallStatistics();
      }
      setLoading(false);
    };

    fetchFriends();
  }, [currentUser]);

  // Function to fetch overall statistics
  const fetchOverallStatistics = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    // Query for settled bets involving the current user
    const betsQuery = query(
      collection(db, 'ActiveBets'),
      where('status', '==', 'Settled'),
      where('user_id', '==', currentUser.uid)
    );

    const acceptorQuery = query(
      collection(db, 'ActiveBets'),
      where('status', '==', 'Settled'),
      where('bet_acceptor_uid', '==', currentUser.uid)
    );

    const [betsSnapshot, acceptorSnapshot] = await Promise.all([getDocs(betsQuery), getDocs(acceptorQuery)]);
    let totalWins = 0;
    let totalLosses = 0;
    let totalAmountWon = 0;

    const allBets = [...betsSnapshot.docs, ...acceptorSnapshot.docs];
    allBets.forEach((doc) => {
      const bet = doc.data();
      const isWinner = bet.winner === currentUser.uid;

      if (isWinner) {
        totalWins++;
        totalAmountWon += parseFloat(bet.payout);
      } else {
        totalLosses++;
        totalAmountWon -= parseFloat(bet.wager_amount);
      }
    });

    setWins(totalWins);
    setLosses(totalLosses);
    setAmountWon(totalAmountWon.toFixed(2));
    setLoading(false);
  };

  // Function to fetch statistics between the current user and the selected friend
  const fetchStatisticsVsFriend = async (friendEmail) => {
    if (!currentUser) return;
    
    setLoading(true);
    // Query to get the friend's user ID from their email
    const friendQuery = query(collection(db, 'users'), where('email', '==', friendEmail));
    const friendSnapshot = await getDocs(friendQuery);
    if (friendSnapshot.empty) {
      setSelectedFriend(null);
      fetchOverallStatistics();
      alert('Friend not found');
      setLoading(false);
      return;
    }

    const friendDoc = friendSnapshot.docs[0];
    const friendId = friendDoc.id;

    // Query for settled bets involving both the current user and the friend
    const betsQuery = query(
      collection(db, 'ActiveBets'),
      where('status', '==', 'Settled'),
      where('user_id', 'in', [currentUser.uid, friendId]),
      where('bet_acceptor_uid', 'in', [currentUser.uid, friendId])
    );
    
    const betsSnapshot = await getDocs(betsQuery);
    let totalWins = 0;
    let totalLosses = 0;
    let totalAmountWon = 0;

    betsSnapshot.forEach((doc) => {
      const bet = doc.data();
      const isWinner = bet.winner === currentUser.uid;

      if (isWinner) {
        totalWins++;
        totalAmountWon += parseFloat(bet.payout);
      } else {
        totalLosses++;
        totalAmountWon -= parseFloat(bet.wager_amount);
      }
    });

    // Update the statistics for the selected friend
    setWins(totalWins);
    setLosses(totalLosses);
    setAmountWon(totalAmountWon.toFixed(2));
    setLoading(false);
  };

  // Handle friend selection
  const handleFriendClick = (friendEmail) => {
    if (selectedFriend === friendEmail) {
      // If the friend is already selected, unselect (show overall stats again)
      setSelectedFriend(null);
      fetchOverallStatistics();
    } else {
      // Fetch statistics vs the selected friend
      setSelectedFriend(friendEmail);
      fetchStatisticsVsFriend(friendEmail);
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="flex-grow flex flex-col bg-gradient-to-b from-gray-900 to-black min-h-screen p-3 sm:p-4 md:p-6">
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl md:border border-gray-800 p-3 sm:p-4 md:p-6 mb-6 md:mb-8">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 flex items-start sm:items-center min-w-0">
          <BarChart2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 mr-2 shrink-0 mt-0.5 sm:mt-0" />
          <span className="min-w-0 break-all">
            {selectedFriend ? `Stats vs: ${selectedFriend}` : 'Overall Stats'}
          </span>
        </h2>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
            <span className="ml-3 text-white">Loading statistics...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-emerald-500/30 transition-all duration-300 p-4 sm:p-5 md:p-6 flex flex-col items-center">
              <div className="bg-emerald-500/20 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
                <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-1">Wins</h3>
              <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{wins}</p>
            </div>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-emerald-500/30 transition-all duration-300 p-4 sm:p-5 md:p-6 flex flex-col items-center">
              <div className="bg-red-500/20 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
                <TrendingDown className="w-8 h-8 sm:w-10 sm:h-10 text-red-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-1">Losses</h3>
              <p className="text-2xl sm:text-3xl font-bold text-red-400">{losses}</p>
            </div>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-emerald-500/30 transition-all duration-300 p-4 sm:p-5 md:p-6 flex flex-col items-center">
              <div className="bg-blue-500/20 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
                <DollarSign className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-1">Amount Won</h3>
              <p className={`text-2xl sm:text-3xl font-bold break-all text-center ${parseFloat(amountWon) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                ${parseFloat(amountWon) >= 0 ? amountWon : Math.abs(parseFloat(amountWon)).toFixed(2)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Friends list */}
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 p-3 sm:p-4 md:p-6">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 flex items-center">
          <User className="w-5 h-5 md:w-6 md:h-6 text-emerald-400 mr-2" />
          Friend Comparisons
        </h2>
        <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6">Click on a friend to view your stats against them.</p>
        
        {friends.length > 0 ? (
          <div className="space-y-3">
            {friends.map((friendEmail, index) => (
              <div 
                key={index}
                className={`flex justify-between items-center p-4 rounded-lg cursor-pointer transition-all duration-300 ${
                  selectedFriend === friendEmail 
                    ? 'bg-emerald-500/20 border border-emerald-500/50' 
                    : 'bg-gray-800/50 border border-gray-700 hover:border-emerald-500/30'
                }`}
                onClick={() => handleFriendClick(friendEmail)}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden border border-gray-600">
                    <img
                      src="https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
                      alt={friendEmail}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-white font-medium text-sm md:text-base truncate min-w-0">{friendEmail}</span>
                </div>
                <BarChart2 className={`w-4 h-4 md:w-5 md:h-5 shrink-0 ${selectedFriend === friendEmail ? 'text-emerald-400' : 'text-gray-400'}`} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-800/30 rounded-lg border border-gray-700">
            <User className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-300">You don't have any friends yet.</p>
            <p className="text-gray-400 text-sm mt-2">Add friends to compare your betting statistics.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Statistics;
